import styled from "styled-components";
import { pxToRem } from "../../styles/utils";

export const SettingsPageContainer = styled.div`
  display: flex;
  height: calc(100% - ${pxToRem(24)});
  margin: ${pxToRem(12)};
`;

export const PageContent = styled.div`
  min-width: 0;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: none;
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  box-shadow: ${({ theme }) => theme.shadows.md}, ${({ theme }) => theme.shadows.ring};

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ContentInner = styled.div`
  width: 100%;
  max-width: ${pxToRem(1180)};
  padding: ${pxToRem(28)} ${pxToRem(32)} ${pxToRem(48)};

  @media (max-width: 900px) {
    padding: ${pxToRem(20)} ${pxToRem(16)} ${pxToRem(40)};
  }
`;

export const PageTitle = styled.h1`
  margin: 0 0 ${pxToRem(6)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(26)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const PageLead = styled.p`
  margin: 0 0 ${pxToRem(8)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(14)};
  line-height: 1.45;
`;

export const SettingsSection = styled.section`
  display: grid;
  grid-template-columns: ${pxToRem(230)} minmax(0, 1fr);
  gap: ${pxToRem(32)};
  padding: ${pxToRem(28)} 0;
  border-top: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${pxToRem(16)};
  }
`;

export const SectionAside = styled.div`
  h2 {
    margin: 0 0 ${pxToRem(6)};
    color: ${({ theme }) => theme.colors.ink};
    font-size: ${pxToRem(16)};
    font-weight: 600;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.inkSoft};
    font-size: ${pxToRem(13)};
    line-height: 1.45;
  }
`;

export const SectionBody = styled.div`
  width: 100%;
  min-width: 0;
`;

export const SettingsRow = styled.div`
  display: grid;
  grid-template-columns: ${pxToRem(140)} minmax(0, 1fr);
  gap: ${pxToRem(18)};
  align-items: start;
  padding: ${pxToRem(14)} 0;
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${pxToRem(6)};
  }
`;

export const RowLabel = styled.div<{ $tight?: boolean }>`
  padding-top: ${({ $tight }) => ($tight ? 0 : pxToRem(11))};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(14)};
  line-height: 1.4;

  @media (max-width: 900px) {
    padding-top: 0;
  }
`;

export const Control = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 0;
`;

export const NameFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${pxToRem(8)};
  width: 100%;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

export const Input = styled.input<{
  $error?: boolean;
  $hasPrefix?: boolean;
  $hasAction?: boolean;
}>`
  width: 100%;
  height: ${pxToRem(50)};
  padding: 0 ${pxToRem(14)};
  padding-left: ${({ $hasPrefix }) => ($hasPrefix ? pxToRem(34) : pxToRem(14))};
  padding-right: ${({ $hasAction }) => ($hasAction ? pxToRem(48) : pxToRem(14))};
  border: ${pxToRem(1)} solid
    ${({ $error }) => ($error ? "rgb(176, 48, 76)" : "rgba(31, 10, 53, 0.26)")};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font: inherit;
  font-size: ${pxToRem(15)};
  font-weight: 400;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.inkMuted};
  }

  &:hover:not(:disabled) {
    border-color: rgba(31, 10, 53, 0.38);
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: inset 0 0 0 ${pxToRem(1)} ${({ theme }) => theme.colors.brand};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const Prefix = styled.span`
  position: absolute;
  left: ${pxToRem(12)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(15)};
  pointer-events: none;
`;

export const PeekButton = styled.button`
  position: absolute;
  right: ${pxToRem(5)};
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(40)};
  height: ${pxToRem(40)};
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: transparent;
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.inkSurface};
    color: ${({ theme }) => theme.colors.ink};
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(1)};
  }

  svg {
    width: ${pxToRem(18)};
    height: ${pxToRem(18)};
  }
`;

export const Hint = styled.div<{ $error?: boolean; $brand?: boolean }>`
  margin-top: ${pxToRem(6)};
  color: ${({ $error, $brand, theme }) =>
    $error ? "rgb(176, 48, 76)" : $brand ? theme.colors.brand : theme.colors.inkMuted};
  font-size: ${pxToRem(12)};
  line-height: 1.4;
`;

export const CodeHint = styled(Hint)`
  display: flex;
  flex-wrap: wrap;
  gap: ${pxToRem(4)};
`;

export const ResendButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: ${({ theme }) => theme.colors.brand};
  font: inherit;
  font-size: ${pxToRem(12)};
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: ${pxToRem(2)} solid ${({ theme }) => theme.colors.brand};
    outline-offset: ${pxToRem(2)};
  }
`;

export const StatusLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(8)};
  padding-top: ${pxToRem(2)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(14)};
`;

export const StatusDot = styled.span<{ $connected: boolean }>`
  width: ${pxToRem(8)};
  height: ${pxToRem(8)};
  border-radius: 50%;
  background: ${({ $connected }) =>
    $connected ? "rgb(75, 158, 106)" : "rgba(31, 10, 53, 0.22)"};
`;

export const StatusNote = styled.div`
  margin-top: ${pxToRem(4)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(13)};
  line-height: 1.4;
`;

export const ActionGroup = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${pxToRem(8)};

  > button {
    width: auto;
    min-width: ${pxToRem(100)};
    max-width: none;
    flex: 0 0 auto;
  }

  @media (max-width: 900px) {
    flex-wrap: wrap;
  }
`;

export const InstructionHint = styled(Hint)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pxToRem(4)};
`;

export const InstructionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(5)};
  color: ${({ theme }) => theme.colors.brand};
  font-size: ${pxToRem(12)};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  svg {
    width: ${pxToRem(15)};
    height: ${pxToRem(15)};
  }
`;

export const ErrorSummary = styled.div`
  margin-top: ${pxToRem(6)};
  color: rgb(176, 48, 76);
  font-size: ${pxToRem(12)};
`;

export const LoadingState = styled.div`
  padding: ${pxToRem(32)} 0;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${pxToRem(14)};
`;
